// Imports
import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faLink } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Tippy from '@tippyjs/react';
import { useRef, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import styled from 'styled-components/macro';
import Button from '../../../../../../../shareables/foundation/front-end/components/buttons/standard';
import Input from '../../../../../../../shareables/foundation/front-end/components/forms/fields/input';
import Select from '../../../../../../../shareables/foundation/front-end/components/forms/fields/select';
import { Label } from '../../../../../../../shareables/foundation/front-end/components/forms/labels';
import MultiFieldContainer from '../../../../../../../shareables/foundation/front-end/components/forms/multi-field-container';
import { visuallyHiddenLabelStyle } from '../../../../../shared-components/visually-hidden-label';
import tippyProps from '../../../../../../../shareables/foundation/front-end/utils/tippy-props';
import { GetProfileResponse } from '../../../../../../../shareables/types/api/main/website/member/settings/profile/get';


// Styled components
const Wrapper = styled.div`
	margin-bottom: 1.25rem;
	
	label {
		${visuallyHiddenLabelStyle}
	}
`;

const DeleteButton = styled(Button)<{ hidden?: boolean }>`
	${(props) => props.hidden && 'visibility: hidden;'}
`;


// Define the accepted props
interface Props {
	socialURLs: GetProfileResponse['socialURLs'];
}


// Function component
const SocialURLs: React.FC<Props> = ({ socialURLs }) => {
	// Use state
	const [amountCount, setAmountCount] = useState(socialURLs.length + 1);
	const [newURLs, setNewURLs] = useState<string[]>(socialURLs.map((temp) => temp.url).concat(''));
	const [toDelete, setToDelete] = useState<number[]>([]);
	const [requireURL, setRequireURL] = useState<boolean[]>(Array.from(socialURLs, () => true).concat(false));
	
	
	// Use ref
	const textInputSetters = useRef<(((value: string) => void) | undefined)[]>(
		Array.from({ length: amountCount + 1 }, () => undefined)
	);
	
	
	// Handle change
	const handleChange = (value: string, index: number) => {
		// Update URLs
		setNewURLs((old) => old.map((curr, targetIndex) => (index === targetIndex ? value : curr)));
		
		
		// Add new field if updating the last field
		if (index === amountCount - 1) {
			setAmountCount((previousAmount) => previousAmount + 1);
			setRequireURL((old) => old.concat(false));
			setNewURLs((old) => old.concat(''));
		}
	};
	
	
	// Handle delete click
	const handleDeleteClick = (index: number) => {
		// Clear input field to allow submission
		textInputSetters.current[index]?.('');
		
		
		// Update delete list
		setToDelete((old) => old.concat([index]));
	};
	
	
	// Return JSX
	return (
		<Wrapper>
			<fieldset>
				<Label as='legend'>Social URLs</Label>
				{Array.from({ length: amountCount + 1 }, (_, i) => (
					<AnimateHeight
						key={i}
						duration={400}
						height={i === amountCount || toDelete.includes(i) ? 0 : 'auto'}
						animateOpacity
					>
						<MultiFieldContainer grow={2}>
							<Select
								name={`socialURLs[${i}].type`}
								label={`Social network ${i + 1}`}
								initialValue={socialURLs[i]?.type ?? undefined}
								required={Boolean(newURLs[i]) && !toDelete.includes(i)}
								exclude={toDelete.includes(i)}
								placeholder='Please select…'
								onChange={() =>
									setRequireURL((old) => old.map((curr, targetIndex) => (targetIndex === i ? true : curr)))
								}
							>
								<option value='FACEBOOK'>Facebook</option>
								<option value='TWITTER'>Twitter</option>
								<option value='INSTAGRAM'>Instagram</option>
								<option value='LINKEDIN'>LinkedIn</option>
								<option value='YOUTUBE'>YouTube</option>
								<option value='PINTEREST'>Pinterest</option>
								<option value='SPOTIFY'>Spotify</option>
								<option value='TUMBLR'>Tumblr</option>
								<option value='MEDIUM'>Medium</option>
								<option value='FOURSQUARE'>Foursquare</option>
								<option value='FLICKR'>Flickr</option>
							</Select>
							
							<Input
								name={`socialURLs[${i}].url`}
								type='url'
								label={`Social URL ${i + 1}`}
								placeholder='https://www.socialnetwork.com/Username'
								initialValue={socialURLs[i]?.url ?? undefined}
								icon={faLink}
								maxLength={100}
								required={requireURL[i] && !toDelete.includes(i)}
								exclude={toDelete.includes(i)}
								onChange={(value) => handleChange(value, i)}
								bindSetter={(setter) => (textInputSetters.current[i] = setter)}
							/>
							
							<Tippy {...tippyProps} content='Delete this social URL'>
								<DeleteButton
									type='button'
									color='danger'
									aria-label='Delete social URL'
									hidden={i === amountCount - 1}
									onClick={() => handleDeleteClick(i)}
								>
									<FontAwesomeIcon icon={faTrash} />
								</DeleteButton>
							</Tippy>
						</MultiFieldContainer>
						
						{i !== amountCount - 1 && <span />}
					</AnimateHeight>
				))}
			</fieldset>
		</Wrapper>
	);
};

export default SocialURLs;
